import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { settingsApi, PublicSettings } from '../api/settingsApi';
import { LoadingStatus } from '../../types';
import { selectActiveSub, selectHasProAccess } from './subscriptionSlice';

interface SettingsState {
  data: PublicSettings | null;
  status: LoadingStatus;
}

const initialState: SettingsState = {
  data: null,
  status: 'idle',
};

export const fetchPublicSettings = createAsyncThunk(
  'settings/fetchPublic',
  async () => {
    const res = await settingsApi.getPublic();
    // apiClient wraps response: res.data is the axios response body { success, data }
    // But apiClient interceptors return res, so res.data = { success, data: PublicSettings }
    const body = res.data as { success: boolean; data: PublicSettings } | PublicSettings;
    return ('data' in body && typeof (body as { data: unknown }).data === 'object'
      ? (body as { data: PublicSettings }).data
      : body) as PublicSettings;
  },
);

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPublicSettings.fulfilled, (state, { payload }) => {
        state.data = payload;
        state.status = 'succeeded';
      })
      .addCase(fetchPublicSettings.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchPublicSettings.rejected, (state) => {
        state.status = 'failed';
      });
  },
});

export default settingsSlice.reducer;

export const selectSettings = (s: { settings: SettingsState }) => s.settings.data;
export const selectSettingsStatus = (s: { settings: SettingsState }) => s.settings.status;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const selectShouldShowAds = (state: any): boolean => {
  if (state.consent?.status !== 'accepted') return false;
  const settings: PublicSettings | null = state.settings?.data ?? null;
  if (!settings || !settings.adsEnabled) return false;
  if (settings.adsTargetMode !== 'specific_plans') return true;
  const hasActiveSub: boolean = selectHasProAccess(state);
  if (!hasActiveSub) return settings.adsIncludeFreeUsers ?? true;
  const planType: string | undefined = selectActiveSub(state)?.planType;
  return planType ? (settings.adsTargetPlanTypes ?? []).includes(planType) : false;
};
